import { api, ApiError } from "@/lib/api";
import { audioContext, ensureRunning } from "./audio";
import { markServerDown, serverStt } from "./engines";
import { concatSamples, encodeWav, resampleLinear, rms, STT_SAMPLE_RATE } from "./wav";

/**
 * Speech to text behind one interface, whichever engine is live:
 *
 * - **server**: the mic is captured here (getUserMedia → ScriptProcessor, which every
 *   browser still runs and needs no worklet file), resampled to 16 kHz mono, sent as a WAV
 *   to whisper.cpp on the board's server.
 * - **browser**: the Web Speech recogniser (`webkitSpeechRecognition`, en-IN), which
 *   captures by itself and gives interim text as it goes.
 *
 * Either way the caller gets a `Dictation`: a level to draw, a promise that settles when
 * speech ends by itself, `stop()` for the text and `cancel()` to throw it away. The mic is
 * released on every way out, so the OS indicator goes off, and a hidden page cancels it.
 */

export type DictationEnd = "silence" | "idle" | "limit" | "manual" | "cancelled" | "error";

export interface Dictation {
  engine: "server" | "browser";
  startedAt: number;
  /** Loudness now, 0..1, for a meter. */
  level(): number;
  /** Settles when capture ends without `stop()`: the browser's recogniser ending, the length cap. */
  ended: Promise<DictationEnd>;
  /** Stop listening and get the text ("" when nothing was heard). */
  stop(): Promise<string>;
  /** Stop listening and discard. */
  cancel(): void;
}

export interface DictationOptions {
  /** Named to the server, so a transcription is logged against the agent it was for. */
  agentId?: string;
  /** Text so far while speaking (the browser engine only; whisper answers at the end). */
  onInterim?: (text: string) => void;
}

/** The server takes 2.5 MB of 16-bit 16 kHz mono ≈ 78 s; stop short of it. */
const MAX_SECONDS = 70;

type RecognitionCtor = new () => SpeechRecognitionLike;
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  onspeechstart: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

const recognitionCtor = (): RecognitionCtor | undefined =>
  typeof window === "undefined"
    ? undefined
    : ((window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor }).SpeechRecognition ??
      (window as unknown as { webkitSpeechRecognition?: RecognitionCtor }).webkitSpeechRecognition);

export const browserRecognitionSupported = (): boolean => Boolean(recognitionCtor());
/** The mic can be captured here (a secure context with getUserMedia and Web Audio). */
export const captureSupported = (): boolean =>
  typeof navigator !== "undefined" && Boolean(navigator.mediaDevices?.getUserMedia) && Boolean(audioContext());

/** Some engine can take dictation on this device right now. */
export const canDictate = (): boolean => (serverStt() && captureSupported()) || browserRecognitionSupported();

const active = new Set<Dictation>();
if (typeof document !== "undefined") {
  // A hidden page must not keep the mic open.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") [...active].forEach((dictation) => dictation.cancel());
  });
}

export async function startDictation(options: DictationOptions = {}): Promise<Dictation> {
  if (serverStt() && captureSupported()) return startServerDictation(options);
  if (browserRecognitionSupported()) return startBrowserDictation(options);
  throw new Error("Voice input is not available in this browser");
}

/* ------------------------------------------------------------------ server */

function micError(cause: unknown): Error {
  const name = (cause as { name?: string })?.name;
  if (name === "NotAllowedError" || name === "SecurityError") return new Error("Microphone access was refused. Allow it in the browser's site settings.");
  if (name === "NotFoundError" || name === "OverconstrainedError") return new Error("No microphone was found");
  if (name === "NotReadableError") return new Error("The microphone is in use by another app");
  return new Error("Could not open the microphone");
}

async function startServerDictation(options: DictationOptions): Promise<Dictation> {
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
    });
  } catch (cause) {
    throw micError(cause);
  }
  const ctx = (await ensureRunning()) ?? audioContext();
  if (!ctx) {
    stream.getTracks().forEach((track) => track.stop());
    throw new Error("Audio is not available in this browser");
  }
  const rate = ctx.sampleRate;
  const source = ctx.createMediaStreamSource(stream);
  const processor = ctx.createScriptProcessor(4096, 1, 1);
  // A ScriptProcessor only runs while connected to the output; a muted gain keeps the mic
  // out of the speakers.
  const mute = ctx.createGain();
  mute.gain.value = 0;
  source.connect(processor);
  processor.connect(mute);
  mute.connect(ctx.destination);

  const blocks: Float32Array[] = [];
  let samples = 0;
  let level = 0;
  let open = true;
  let settle!: (end: DictationEnd) => void;
  const ended = new Promise<DictationEnd>((resolve) => (settle = resolve));

  processor.onaudioprocess = (event) => {
    if (!open) return;
    const block = new Float32Array(event.inputBuffer.getChannelData(0));
    blocks.push(block);
    samples += block.length;
    const loudness = rms(block);
    level = Math.max(loudness, level * 0.6);
    if (samples / rate >= MAX_SECONDS) settle("limit");
  };

  const release = () => {
    if (!open) return;
    open = false;
    processor.onaudioprocess = null;
    try {
      source.disconnect();
      processor.disconnect();
      mute.disconnect();
    } catch {
      // Already apart.
    }
    stream.getTracks().forEach((track) => track.stop());
    active.delete(dictation);
  };

  const dictation: Dictation = {
    engine: "server",
    startedAt: Date.now(),
    level: () => Math.min(1, Math.pow(level * 9, 0.7)),
    ended,
    stop: async () => {
      release();
      settle("manual");
      const pcm = concatSamples(blocks);
      // Under a third of a second is a tap, not speech.
      if (pcm.length < rate * 0.3) return "";
      const wav = new Blob([encodeWav(await to16k(pcm, rate))], { type: "audio/wav" });
      return transcribeWav(wav, options.agentId);
    },
    cancel: () => {
      release();
      settle("cancelled");
    },
  };
  active.add(dictation);
  return dictation;
}

/** 16 kHz mono through the browser's resampler, or the plain one where it has none. */
async function to16k(pcm: Float32Array, rate: number): Promise<Float32Array> {
  if (rate === STT_SAMPLE_RATE) return pcm;
  const Offline =
    window.OfflineAudioContext ?? (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
  if (!Offline) return resampleLinear(pcm, rate, STT_SAMPLE_RATE);
  try {
    const offline = new Offline(1, Math.ceil((pcm.length * STT_SAMPLE_RATE) / rate), STT_SAMPLE_RATE);
    const buffer = offline.createBuffer(1, pcm.length, rate);
    buffer.getChannelData(0).set(pcm);
    const node = offline.createBufferSource();
    node.buffer = buffer;
    node.connect(offline.destination);
    node.start();
    const rendered = await Promise.race([
      offline.startRendering(),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
    ]);
    return rendered ? new Float32Array(rendered.getChannelData(0)) : resampleLinear(pcm, rate, STT_SAMPLE_RATE);
  } catch {
    return resampleLinear(pcm, rate, STT_SAMPLE_RATE);
  }
}

/** POST the WAV; a busy engine (429) gets one more try a second later. */
async function transcribeWav(wav: Blob, agentId?: string): Promise<string> {
  for (let attempt = 0; ; attempt++) {
    try {
      return (await api.transcribe(wav, agentId)).text.trim();
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 429 && attempt === 0) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        continue;
      }
      if (cause instanceof ApiError && cause.status === 429) throw new Error("Voice input is busy. Try again in a moment.");
      // Anything else: the next dictation uses the browser's recogniser where there is one.
      if (browserRecognitionSupported()) markServerDown();
      throw new Error(cause instanceof ApiError ? `Could not transcribe: ${cause.message}` : "Could not transcribe that");
    }
  }
}

/* ------------------------------------------------------------------ browser */

function startBrowserDictation(options: DictationOptions): Promise<Dictation> {
  const Recognition = recognitionCtor()!;
  const recognition = new Recognition();
  recognition.lang = "en-IN";
  recognition.interimResults = true;
  recognition.maxAlternatives = 1;
  // The composer keeps listening until the user stops it.
  recognition.continuous = true;

  let finalText = "";
  let interim = "";
  let speaking = false;
  let failure: string | null = null;
  let finished = false;
  let settle!: (end: DictationEnd) => void;
  const ended = new Promise<DictationEnd>((resolve) => (settle = resolve));
  let onFinish: (() => void) | null = null;
  const text = () => `${finalText} ${interim}`.replace(/\s+/g, " ").trim();

  recognition.onspeechstart = () => {
    speaking = true;
  };
  recognition.onresult = (event) => {
    let nextFinal = "";
    interim = "";
    for (let i = 0; i < event.results.length; i++) {
      const result = event.results[i]!;
      if (result.isFinal) nextFinal += `${result[0].transcript} `;
      else interim += result[0].transcript;
    }
    finalText = nextFinal.trim();
    speaking = true;
    options.onInterim?.(text());
  };
  recognition.onerror = (event) => {
    failure = event.error;
  };
  recognition.onend = () => {
    finished = true;
    active.delete(dictation);
    if (failure && failure !== "no-speech" && failure !== "aborted") settle("error");
    else settle(text() ? "silence" : "idle");
    onFinish?.();
  };

  const waitForEnd = () =>
    new Promise<void>((resolve) => {
      if (finished) return resolve();
      onFinish = resolve;
      // A recogniser that never ends must not hang the caller.
      setTimeout(resolve, 2500);
    });

  const dictation: Dictation = {
    engine: "browser",
    startedAt: Date.now(),
    // No audio to measure here; a gentle pulse while speech is heard stands in.
    level: () => (speaking && !finished ? 0.35 + 0.25 * Math.abs(Math.sin(Date.now() / 140)) : 0.04),
    ended,
    stop: async () => {
      if (!finished) {
        try {
          recognition.stop();
        } catch {
          // Already stopped.
        }
        await waitForEnd();
      }
      if (failure === "not-allowed" || failure === "service-not-allowed") {
        throw new Error("Microphone access was refused. Allow it in the browser's site settings.");
      }
      return text();
    },
    cancel: () => {
      options.onInterim = undefined;
      try {
        recognition.abort();
      } catch {
        // Already stopped.
      }
      settle("cancelled");
    },
  };

  return new Promise<Dictation>((resolve, reject) => {
    try {
      recognition.start();
    } catch (cause) {
      reject(cause instanceof Error ? cause : new Error("Could not start voice input"));
      return;
    }
    active.add(dictation);
    resolve(dictation);
  });
}
